import { AutomationActionType } from "@/api/types/automation";
import type { FlowNode, FlowNodeAction } from "@/api/types/flow";
import type { FlowStateWithFlow } from "@/api/types/flow-state";

/**
 * Strips the [*] system-flow marker from flow names, including names
 * embedded mid-sentence in generated copy.
 */
export function formatFlowName(name: string): string {
  return name.replace(/\[\*\]\s*/g, "");
}

/**
 * Find a node by ID in the flow tree
 */
export function findNodeById(node: FlowNode | null, nodeId: string): FlowNode | null {
  if (!node) return null;
  if (node.id === nodeId) return node;

  for (const child of node.children) {
    const found = findNodeById(child, nodeId);
    if (found) return found;
  }
  return null;
}

/**
 * Find the next action node after the given node.
 * Traverses through timer, filter nodes to find the actual action.
 * Returns null if we hit a conditional/branch since we can't know which path will be taken.
 */
export function findNextActionNode(node: FlowNode | null): FlowNodeAction | null {
  if (!node) return null;

  // Check children for the next action
  for (const child of node.children) {
    if (!child) continue;
    if (child.type === "action") return child as FlowNodeAction;
    // Stop at conditional/branch nodes - we can't know which path will be taken
    if (child.type === "conditional" || child.type === "branch") return null;
    // Recursively search through timer, filter, root nodes
    const actionInChild = findNextActionNode(child);
    if (actionInChild) return actionInChild;
  }
  return null;
}

/**
 * Get a human-readable label for an action type
 */
export function getActionLabel(actionType: AutomationActionType): string {
  switch (actionType) {
    case "outgoing_message":
      return "Sends message";
    case "outgoing_message_multichannel":
      return "Sends text or email";
    case "add_to_list":
      return "Adds to list";
    case "remove_from_list":
      return "Removes from list";
    case "webhook":
      return "Triggers webhook";
    default:
      return "Next action";
  }
}

/**
 * Get the description of the next action for a flow state
 */
export function getNextActionDescription(flowState: FlowStateWithFlow): string | null {
  const flow = flowState.flow;
  if (!flow.node) return null;

  // Find the current node in the flow tree
  const currentNode = findNodeById(flow.node, flowState.node);
  if (!currentNode) return null;

  // Find the next action after the current node
  const nextAction = findNextActionNode(currentNode);
  if (!nextAction || !nextAction.options?.actions?.length) return null;

  // Get the first action's type
  const firstAction = nextAction.options.actions[0];
  return getActionLabel(firstAction.type as AutomationActionType);
}
